import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Avatar,
  Tag,
  Space,
  Button,
  Typography,
  Image,
  Row,
  Col,
  message,
  Spin,
  Empty,
  Input,
  Modal,
  Form,
  Popconfirm,
  Switch,
  DatePicker,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { postsService, Post } from '../services/postsService';
import { getImageUrl } from '../config/api';
import apiClient from '../services/apiClient';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState<Post | null>(null);
  const [searchId, setSearchId] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [suspendModalVisible, setSuspendModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [suspendForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchPost(id);
    }
  }, [id]);

  const fetchPost = async (postId: string) => {
    try {
      setLoading(true);
      const postData = await postsService.getPostById(postId);
      setPost(postData);
    } catch (error: any) {
      console.error('Error fetching post:', error);
      message.error(error.message || 'Failed to fetch post');
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchId.trim()) {
      navigate(`/posts/${searchId.trim()}`);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ padding: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/posts')}
          style={{ marginBottom: 16 }}
        >
          Back to Posts
        </Button>
        <Card>
          <Empty description="Post not found" />
          <div style={{ marginTop: 24 }}>
            <Title level={4}>Search Post by ID</Title>
            <Space.Compact style={{ width: '100%', maxWidth: 500 }}>
              <Input
                placeholder="Enter Post ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                onPressEnter={handleSearch}
              />
              <Button type="primary" onClick={handleSearch}>
                Search
              </Button>
            </Space.Compact>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/posts')}
        style={{ marginBottom: 16 }}
      >
        Back to Posts
      </Button>

      <Card>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar
                src={getImageUrl(post.user.profileImage || '')}
                icon={<UserOutlined />}
                size={80}
                style={{ marginBottom: 16 }}
              />
              <Title level={4} style={{ marginBottom: 8 }}>
                {post.user.name}
              </Title>
              <Text type="secondary">@{post.user.username}</Text>
              <div style={{ marginTop: 16 }}>
                <Space wrap>
                  {post.user.isAdmin && <Tag color="purple">Admin</Tag>}
                  {post.user.isVerified && <Tag color="green">Verified</Tag>}
                </Space>
              </div>
            </div>
          </Col>

          <Col xs={24} md={16}>
            <Descriptions title="Post Information" bordered column={2}>
              <Descriptions.Item label="Post ID" span={2}>
                <Text copyable>{post._id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Space wrap>
                  <Tag color={post.isActive ? 'green' : 'red'}>
                    {post.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                  {post.isSuspended && (
                    <Tag color="orange">
                      Suspended
                      {post.suspendedUntil && new Date(post.suspendedUntil) > new Date() && (
                        <span> until {new Date(post.suspendedUntil).toLocaleDateString()}</span>
                      )}
                    </Tag>
                  )}
                  {post.isRejected && (
                    <Tag color="red">Rejected</Tag>
                  )}
                </Space>
              </Descriptions.Item>
              {post.suspensionReason && (
                <Descriptions.Item label="Suspension Reason" span={2}>
                  {post.suspensionReason}
                </Descriptions.Item>
              )}
              {post.rejectionReason && (
                <Descriptions.Item label="Rejection Reason" span={2}>
                  {post.rejectionReason}
                </Descriptions.Item>
              )}
              {post.rejectedAt && (
                <Descriptions.Item label="Rejected At" span={2}>
                  {new Date(post.rejectedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created">
                {post.createdAt ? new Date(post.createdAt).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Likes">
                {post.likesCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Comments">
                {post.commentsCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Caption" span={2}>
                <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                  {post.caption || '-'}
                </Paragraph>
              </Descriptions.Item>
              {post.location && (
                <Descriptions.Item label="Location" span={2}>
                  {post.location.name || 'Unknown'}
                  {post.location.coordinates && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({post.location.coordinates[1]}, {post.location.coordinates[0]})
                    </Text>
                  )}
                </Descriptions.Item>
              )}
              {post.tags && post.tags.length > 0 && (
                <Descriptions.Item label="Tags" span={2}>
                  <Space wrap>
                    {post.tags.map((tag, index) => (
                      <Tag key={index} color="blue">#{tag}</Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 24 }}>
              <Space wrap>
                <Button icon={<EditOutlined />} onClick={() => {
                  form.setFieldsValue({
                    caption: post.caption || '',
                    tags: post.tags ? post.tags.join(', ') : '',
                    locationName: post.location?.name || '',
                    coordinates: post.location?.coordinates ? `${post.location.coordinates[1]}, ${post.location.coordinates[0]}` : '',
                    isActive: post.isActive,
                  });
                  setEditModalVisible(true);
                }}>
                  Edit Post
                </Button>
                {post.isRejected ? (
                  <Button
                    type="primary"
                    onClick={() => message.info('Post is rejected. Cannot perform other actions.')}
                  >
                    Post Rejected
                  </Button>
                ) : post.isSuspended ? (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={async () => {
                      try {
                        await postsService.unsuspendPost(post._id);
                        message.success('Post unsuspended successfully');
                        fetchPost(post._id);
                      } catch (error: any) {
                        message.error(error.message || 'Failed to unsuspend post');
                      }
                    }}
                  >
                    Unsuspend Post
                  </Button>
                ) : (
                  <>
                    <Button
                      icon={<StopOutlined />}
                      onClick={() => {
                        suspendForm.resetFields();
                        setSuspendModalVisible(true);
                      }}
                    >
                      Suspend Post
                    </Button>
                    <Button
                      danger
                      onClick={() => {
                        rejectForm.resetFields();
                        setRejectModalVisible(true);
                      }}
                    >
                      Reject Post
                    </Button>
                    <Button
                      icon={post.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={async () => {
                        try {
                          await postsService.updatePost(post._id, { isActive: !post.isActive });
                          message.success(`Post ${!post.isActive ? 'activated' : 'deactivated'} successfully`);
                          fetchPost(post._id);
                        } catch (error: any) {
                          message.error(error.message || 'Failed to toggle post status');
                        }
                      }}
                    >
                      {post.isActive ? 'Hide Post' : 'Show Post'}
                    </Button>
                  </>
                )}
                <Popconfirm
                  title="Are you sure you want to delete this post?"
                  onConfirm={async () => {
                    try {
                      await apiClient.delete(`/posts/admin/${post._id}`);
                      message.success('Post deleted successfully');
                      navigate('/posts');
                    } catch (error: any) {
                      message.error(error.response?.data?.message || 'Failed to delete post');
                    }
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger>
                    Delete Post
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {post.media && post.media.length > 0 && (
        <Card title="Media" style={{ marginTop: 24 }}>
          <Row gutter={[16, 16]}>
            {post.media
              .sort((a, b) => a.order - b.order)
              .map((media, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <div style={{ position: 'relative' }}>
                    {media.type === 'image' ? (
                      <Image
                        src={getImageUrl(media.url)}
                        alt={`Post media ${index + 1}`}
                        style={{ width: '100%', borderRadius: 8 }}
                        fallback="https://via.placeholder.com/300x300"
                      />
                    ) : (
                      <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000', borderRadius: 8 }}>
                        <video
                          src={getImageUrl(media.url)}
                          controls
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 8 }}
                        />
                      </div>
                    )}
                    <Tag
                      color={media.type === 'image' ? 'blue' : 'purple'}
                      style={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      {media.type.toUpperCase()}
                    </Tag>
                  </div>
                </Col>
              ))}
          </Row>
        </Card>
      )}

      <Card style={{ marginTop: 24 }}>
        <Title level={4}>Search Another Post</Title>
        <Space.Compact style={{ width: '100%', maxWidth: 500 }}>
          <Input
            placeholder="Enter Post ID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onPressEnter={handleSearch}
          />
          <Button type="primary" onClick={handleSearch}>
            Search
          </Button>
        </Space.Compact>
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Post"
        open={editModalVisible}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            const updateData: any = {
              caption: values.caption,
              tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : [],
              isActive: values.isActive,
            };

            if (values.locationName || values.coordinates) {
              updateData.location = {
                name: values.locationName || '',
              };
              if (values.coordinates) {
                const [lat, lng] = values.coordinates.split(',').map((c: string) => parseFloat(c.trim()));
                if (!isNaN(lat) && !isNaN(lng)) {
                  updateData.location.coordinates = [lng, lat]; // [longitude, latitude]
                }
              }
            }

            await postsService.updatePost(post!._id, updateData);
            message.success('Post updated successfully');
            setEditModalVisible(false);
            fetchPost(post!._id);
          } catch (error: any) {
            if (error.errorFields) {
              // Form validation errors
              return;
            }
            message.error(error.message || 'Failed to update post');
          }
        }}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        okText="Save"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="caption"
            label="Caption"
            rules={[{ max: 2000, message: 'Caption cannot exceed 2000 characters' }]}
          >
            <TextArea rows={4} placeholder="Enter post caption" />
          </Form.Item>
          <Form.Item
            name="tags"
            label="Tags (comma-separated)"
            help="Enter tags separated by commas, e.g., dog, pet, cute"
          >
            <Input placeholder="Enter tags separated by commas" />
          </Form.Item>
          <Form.Item
            name="locationName"
            label="Location Name"
          >
            <Input placeholder="Enter location name" />
          </Form.Item>
          <Form.Item
            name="coordinates"
            label="Coordinates (Latitude, Longitude)"
            help="Format: latitude, longitude (e.g., 28.7041, 77.1025)"
          >
            <Input placeholder="28.7041, 77.1025" />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Suspend Modal */}
      <Modal
        title="Suspend Post Temporarily"
        open={suspendModalVisible}
        onOk={async () => {
          try {
            const values = await suspendForm.validateFields();
            const suspendedUntil = values.suspendedUntil 
              ? values.suspendedUntil.toISOString() 
              : undefined;
            
            await postsService.suspendPost(post!._id, values.reason, suspendedUntil);
            message.success('Post suspended successfully');
            setSuspendModalVisible(false);
            suspendForm.resetFields();
            fetchPost(post!._id);
          } catch (error: any) {
            if (error.errorFields) {
              // Form validation errors
              return;
            }
            message.error(error.message || 'Failed to suspend post');
          }
        }}
        onCancel={() => {
          setSuspendModalVisible(false);
          suspendForm.resetFields();
        }}
        okText="Suspend"
        okButtonProps={{ danger: true }}
        width={500}
      >
        <Form form={suspendForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason for Suspension"
            rules={[{ required: false }]}
          >
            <TextArea rows={3} placeholder="Enter reason for suspension (optional)" />
          </Form.Item>
          <Form.Item
            name="suspendedUntil"
            label="Suspend Until (Optional)"
            help="Leave empty for default 7 days suspension"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="Select date (default: 7 days)"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Post"
        open={rejectModalVisible}
        onOk={async () => {
          try {
            const values = await rejectForm.validateFields();
            await postsService.rejectPost(post!._id, values.reason);
            message.success('Post rejected successfully and user has been notified');
            setRejectModalVisible(false);
            rejectForm.resetFields();
            fetchPost(post!._id);
          } catch (error: any) {
            if (error.errorFields) {
              // Form validation errors
              return;
            }
            message.error(error.message || 'Failed to reject post');
          }
        }}
        onCancel={() => {
          setRejectModalVisible(false);
          rejectForm.resetFields();
        }}
        okText="Reject Post"
        okButtonProps={{ danger: true }}
        width={500}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: 'Please provide a reason for rejection' }]}
          >
            <TextArea rows={4} placeholder="Enter reason for rejecting this post (required)" />
          </Form.Item>
          <div style={{ padding: '12px', background: '#fff7e6', borderRadius: 4, marginTop: 8 }}>
            <Text type="warning">
              ⚠️ This action will deactivate the post and send a notification to the user.
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PostDetail;

